import { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import patientFieldConfigService, { PatientFieldConfig } from '../services/patientFieldConfigService';
import { ColumnConfig } from './useTablePreferences';
import { FieldMeta } from '../config/fieldMetadata';

export function usePatientFieldConfigs(patientTypeId?: number) {
  const { currentTenant } = useTenant();
  const [fieldConfigs, setFieldConfigs] = useState<PatientFieldConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentTenant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    patientFieldConfigService.getAll(currentTenant.id, patientTypeId)
      .then(setFieldConfigs)
      .catch(err => console.error('Failed to load patient field configs:', err))
      .finally(() => setLoading(false));
  }, [currentTenant, patientTypeId]);

  const dynamicColumns: ColumnConfig[] = fieldConfigs.map(config => ({
    key: `df_${config.key}`,
    label: config.label,
    defaultWidth: 100,
    minWidth: 80,
    visible: false,
    sortable: true,
    resizable: true,
    draggable: true,
  }));

  const dynamicFieldMeta: FieldMeta[] = fieldConfigs.map(config => {
    let dataType: FieldMeta['dataType'];
    switch (config.field_type) {
      case 'number': dataType = 'number'; break;
      case 'date': dataType = 'date'; break;
      case 'bool': dataType = 'enum'; break;
      case 'single_select': dataType = 'enum'; break;
      default: dataType = 'text';
    }

    let enumOptions: { value: string; label: string }[] | undefined;
    if (config.field_type === 'bool') {
      enumOptions = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
    } else if (config.field_type === 'single_select' && config.options) {
      enumOptions = config.options;
    }

    return {
      key: `df_${config.key}`,
      label: config.label,
      dataType,
      enumOptions,
    };
  });

  return { fieldConfigs, dynamicColumns, dynamicFieldMeta, loading };
}
